import { useMemo, useState } from "react";
import { renderBroadcastCell } from "./broadcast";
import { ConferenceLink } from "./ConferenceLink";
import { SortHeader, type SortSpec, toggleSort } from "./SortHeader";
import { TeamLink } from "./TeamLink";
import { TeamLogo } from "./TeamLogo";
import { useFetch } from "./useFetch";
import type {
  ConferencesResponse,
  Game,
  GameSide,
  GameState,
  Program,
  RosterPlayer,
  RosterResponse,
  Standing,
  StandingsResponse,
  TeamGamesResponse,
  TeamsResponse,
  WLT,
} from "./types";

const SPORT_LABEL: Record<string, string> = {
  "soccer-women": "Women's Soccer",
  "soccer-men": "Men's Soccer",
  football: "Football",
  "basketball-women": "Women's Basketball",
  "basketball-men": "Men's Basketball",
};

// toUnderscoreSlug converts the hyphen-cased team slug the scoreboard
// emits into the underscore-cased slug the catalog keys programs by.
// The two forms always correspond; a round-trip through this helper
// loses no information.
function toUnderscoreSlug(slug: string): string {
  return slug.replace(/-/g, "_");
}

function titleCase(slug: string): string {
  return slug
    .split(/[-_]/)
    .filter((p) => p.length > 0)
    .map((p) => p[0].toUpperCase() + p.slice(1))
    .join(" ");
}

function wlt(r: WLT): string {
  return `${r.wins}-${r.losses}-${r.ties}`;
}

// outcomeFor picks the win/loss/tie outcome from the picked team's
// perspective on a final game. "" for non-final games or the rare
// edge where both sides have winner=false and the game is final
// (treated as tie).
function outcomeFor(
  g: Game,
  side: "home" | "away",
): "W" | "L" | "T" | "" {
  if (g.state !== "final") return "";
  const me = side === "home" ? g.home : g.away;
  const them = side === "home" ? g.away : g.home;
  if (me.winner) return "W";
  if (them.winner) return "L";
  return "T";
}

// formatDate renders an ISO date-time as "Aug 13" style. The games
// endpoint publishes `date` as midnight-UTC; using toLocaleDateString
// without a timezone would occasionally slide a game to the prior
// calendar day in US zones, so we format off the UTC components —
// NCAA's "Oct 15 slate" should read as "Oct 15" everywhere, same
// label regardless of viewer's zone.
function formatDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

// formatKickoff renders a start-time ISO as "7:00 PM EST" style, in
// the viewer's local timezone. Including the timezone abbreviation
// is deliberate — a time without a zone is ambiguous, and the whole
// point of displaying kickoff is for the reader not to miss the
// match. Returns "TBA" when the upstream didn't publish a time
// (preseason, postponed, some canceled rows). Shared across the
// team-page schedule and the daily scoreboard so the two views
// render the same format.
function formatKickoff(iso: string): string {
  if (!iso) return "TBA";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return "TBA";
  // Postgres TIMESTAMPTZ null round-trips as "0001-01-01T00:00:00Z"
  // through the standings adapter; treat Year < 1900 as "no time
  // published" so the UI emits TBA instead of "6:53 PM LMT".
  if (t.getUTCFullYear() < 1900) return "TBA";
  return t.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
}

export type TeamPageProps = {
  sport: string;
  division: string;
  slug: string;
};

export function TeamPage({ sport, division, slug }: TeamPageProps) {
  // Which tab is visible under the stats grid. Local state (not URL-
  // backed) because the choice is ephemeral — a shared team link
  // shouldn't preserve "I was last looking at the schedule." Default
  // is roster because a reader hitting a team page usually wants
  // to see who's on it first.
  const [tab, setTab] = useState<"roster" | "schedule">("roster");

  // Standings fetch dispatches by sport. /v1/standings only serves
  // RPI-ranked sports (soccer); football and basketball have
  // dedicated endpoints with records-based shapes. All three shapes
  // carry teamSlug + rank for the schedule's rank-per-opponent
  // column; the soccer-only StatsPanel stays gated behind a
  // non-null standing so it hides for other sports.
  const isSoccer = sport === "soccer-women" || sport === "soccer-men";
  const standingsFetchURL = isSoccer
    ? `/api/v1/standings?sport=${encodeURIComponent(sport)}&division=${encodeURIComponent(division)}`
    : "";
  const recordsFetchURL = isSoccer
    ? ""
    : sport === "football"
      ? `/api/v1/standings/football/${encodeURIComponent(division)}`
      : sport === "basketball-men" || sport === "basketball-women"
        ? `/api/v1/standings/${encodeURIComponent(sport)}/${encodeURIComponent(division)}`
        : "";
  const standingsState = useFetch<StandingsResponse>(standingsFetchURL);
  const recordsState = useFetch<{
    standings: { teamSlug: string; rank: number }[];
  }>(recordsFetchURL);
  const teamsState = useFetch<TeamsResponse>(
    `/api/v1/teams?sport=${encodeURIComponent(sport)}&division=${encodeURIComponent(division)}`,
  );
  const confsState = useFetch<ConferencesResponse>("/api/v1/conferences");
  const programState = useFetch<Program>(
    `/api/v1/programs/${encodeURIComponent(toUnderscoreSlug(slug))}?sport=${encodeURIComponent(sport)}`,
  );
  const gamesState = useFetch<TeamGamesResponse>(
    `/api/v1/teams/${encodeURIComponent(sport)}/${encodeURIComponent(division)}/${encodeURIComponent(slug)}/games`,
  );
  const rosterState = useFetch<RosterResponse>(
    `/api/v1/programs/${encodeURIComponent(toUnderscoreSlug(slug))}/roster?sport=${encodeURIComponent(sport)}`,
  );

  // Resolve a human-readable name for any slug (this team OR an
  // opponent on the schedule) from the catalog's teams list. Both
  // hyphen and underscore forms are keyed so hyphens from the
  // scoreboard and underscores from the catalog resolve. Fallback
  // is a title-cased slug so the UI never shows the raw hyphen form.
  const nameFor = useMemo(() => {
    const map = new Map<string, string>();
    if (teamsState.kind === "ok") {
      for (const t of teamsState.data.teams) {
        map.set(t.slug, t.shortName);
        map.set(t.slug.replace(/_/g, "-"), t.shortName);
      }
    }
    return (s: string): string => map.get(s) ?? titleCase(s);
  }, [teamsState]);

  // Slug → rank. Built from whichever standings endpoint responded
  // for the current sport (soccer: /v1/standings; football/basketball:
  // their per-sport endpoints). Both response shapes carry
  // teamSlug + rank, so the schedule's rank-per-opponent column
  // populates uniformly. Both hyphen + underscore slug forms key
  // the map because the scoreboard emits hyphen slugs and the
  // standings service persists whatever ingest wrote.
  const rankFor = useMemo(() => {
    const map = new Map<string, number>();
    const rows: { teamSlug: string; rank: number }[] =
      standingsState.kind === "ok"
        ? standingsState.data.standings
        : recordsState.kind === "ok"
          ? recordsState.data.standings
          : [];
    for (const s of rows) {
      map.set(s.teamSlug, s.rank);
      map.set(s.teamSlug.replace(/_/g, "-"), s.rank);
      map.set(s.teamSlug.replace(/-/g, "_"), s.rank);
    }
    return (s: string): number | undefined => map.get(s);
  }, [standingsState, recordsState]);

  // Slug → conference short label. Falls through to an upper-cased
  // rendering of the slug so an unknown conference still reads
  // reasonably in the schedule's Conf column.
  const confLabelFor = useMemo(() => {
    const map = new Map<string, string>();
    if (confsState.kind === "ok") {
      for (const c of confsState.data.conferences) {
        map.set(c.slug, c.shortName);
        map.set(c.slug.replace(/_/g, "-"), c.shortName);
      }
    }
    return (s: string): string => {
      if (!s) return "";
      return map.get(s) ?? s.toUpperCase().replace(/[-_]/g, " ");
    };
  }, [confsState]);

  // Prefer the Program display name for the current team because it
  // reads more naturally than the catalog shortName (e.g. "North
  // Carolina" vs "UNC"). Falls through to nameFor otherwise.
  const teamName = useMemo(() => {
    if (programState.kind === "ok" && programState.data.name) {
      return programState.data.name;
    }
    return nameFor(slug);
  }, [programState, nameFor, slug]);

  // Pull this team's Standing row from the standings response. Both
  // hyphen and underscore forms are matched because the standings
  // service stores whatever ingest wrote.
  const standing = useMemo<Standing | null>(() => {
    if (standingsState.kind !== "ok") return null;
    for (const s of standingsState.data.standings) {
      if (s.teamSlug === slug || s.teamSlug.replace(/_/g, "-") === slug) {
        return s;
      }
    }
    return null;
  }, [standingsState, slug]);

  // Conference slug and display label. Prefer the standings row (reflects
  // what the team actually plays in this season) and fall back to the
  // program record so the chip still shows when the team hasn't played
  // yet. The slug drives the ConferenceLink destination; the label
  // comes from catalog if known, otherwise the slug upper-cased.
  const confSlug =
    standing?.conference ??
    (programState.kind === "ok" ? programState.data.conference : "");
  const conferenceLabel = useMemo(() => {
    if (!confSlug) return "";
    if (confsState.kind === "ok") {
      for (const c of confsState.data.conferences) {
        if (c.slug === confSlug || c.slug.replace(/_/g, "-") === confSlug) {
          return c.shortName;
        }
      }
    }
    return confSlug.toUpperCase().replace(/[-_]/g, " ");
  }, [confSlug, confsState]);

  return (
    <section>
      <div className="page-head team-head">
        <TeamLogo slug={slug} alt={teamName} />
        <h2>
          {teamName}{" "}
          <span className="muted">
            {division.toUpperCase()} {SPORT_LABEL[sport] ?? sport}
            {conferenceLabel ? " · " : ""}
            {conferenceLabel && confSlug ? (
              <ConferenceLink
                conference={confSlug}
                sport={sport}
                division={division}
                label={conferenceLabel}
              />
            ) : (
              conferenceLabel
            )}
          </span>
        </h2>
      </div>

      {standing && (
        <StatsPanel
          s={standing}
          rosterCount={rosterState.kind === "ok" ? rosterState.data.count : undefined}
        />
      )}
      {standing === null && standingsState.kind === "ok" && (
        <p className="muted">
          No standings row yet for this team.
        </p>
      )}
      {standingsState.kind === "error" && (
        <p role="alert">Failed to load standings: {standingsState.message}</p>
      )}

      <ExternalLinks program={programState.kind === "ok" ? programState.data : null} />

      {/* Tabs reduce scrolling: Roster and Schedule are both tall
          tables; stacking them pushed Schedule below the fold. The
          tablist uses the ARIA tablist / tab / tabpanel pattern so
          screen readers announce "tab 1 of 2, selected" correctly.
          Default landing tab is Roster — a reader opening a team
          page usually wants to see who's on it first. */}
      <div className="tabs" role="tablist" aria-label="Team sections">
        <button
          type="button"
          role="tab"
          id="tab-roster"
          aria-controls="panel-roster"
          aria-selected={tab === "roster"}
          className={tab === "roster" ? "active" : undefined}
          onClick={() => setTab("roster")}
        >
          Roster
        </button>
        <button
          type="button"
          role="tab"
          id="tab-schedule"
          aria-controls="panel-schedule"
          aria-selected={tab === "schedule"}
          className={tab === "schedule" ? "active" : undefined}
          onClick={() => setTab("schedule")}
        >
          Schedule
        </button>
      </div>

      {tab === "roster" && (
        <div
          role="tabpanel"
          id="panel-roster"
          aria-labelledby="tab-roster"
        >
          {rosterState.kind === "loading" && <p role="status">Loading…</p>}
          {rosterState.kind === "error" && (
            <p role="alert">Failed to load roster: {rosterState.message}</p>
          )}
          {rosterState.kind === "ok" &&
            (rosterState.data.count === 0 ? (
              <p className="muted">
                Roster unavailable — either the ingest-worker hasn't
                scraped this program yet, or the program's CMS is not
                yet supported.
              </p>
            ) : (
              <RosterPanel
                players={rosterState.data.players}
                isSoccer={isSoccer}
              />
            ))}
        </div>
      )}

      {tab === "schedule" && (
        <div
          role="tabpanel"
          id="panel-schedule"
          aria-labelledby="tab-schedule"
        >
          {gamesState.kind === "loading" && <p role="status">Loading…</p>}
          {gamesState.kind === "error" && (
            <p role="alert">Failed to load schedule: {gamesState.message}</p>
          )}
          {gamesState.kind === "ok" &&
            (gamesState.data.count === 0 ? (
              <p className="muted">No games scheduled yet.</p>
            ) : (
              <ScheduleTable
                games={gamesState.data.games}
                forSlug={slug}
                sport={sport}
                division={division}
                nameFor={nameFor}
                rankFor={rankFor}
                confLabelFor={confLabelFor}
              />
            ))}
        </div>
      )}
    </section>
  );
}

function StatsPanel({
  s,
  rosterCount,
}: {
  s: Standing;
  // rosterCount is undefined while the /roster fetch is loading or
  // failed. Rendered as em-dash in that case so the card still shows
  // up (keeping the grid shape stable) but visually reads as "no
  // data yet" rather than "0 players."
  rosterCount: number | undefined;
}) {
  const overall: WLT = { wins: s.wins, losses: s.losses, ties: s.ties };
  return (
    <div role="group" aria-label="Team stats">
      <div className="stats-hero">
        <StatCard label="RPI Rank" value={String(s.rank)} />
        <StatCard label="RPI" value={s.rpi.toFixed(4)} />
        <StatCard label="Overall" value={wlt(overall)} />
        <StatCard label="Conference" value={wlt(s.conferenceRecord)} />
      </div>
      <div className="stats-grid">
        <StatCard label="Non-Conference" value={wlt(s.nonConferenceRecord)} />
        <StatCard label="Home" value={wlt(s.homeRecord)} />
        <StatCard label="Road" value={wlt(s.roadRecord)} />
        <StatCard label="Neutral" value={wlt(s.neutralRecord)} />
        <StatCard label="Non-D1" value={wlt(s.nonDivisionRecord)} />
        <StatCard
          label="Players"
          value={rosterCount === undefined ? "—" : String(rosterCount)}
        />
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

function ExternalLinks({ program }: { program: Program | null }) {
  // When no program record exists (sport/division not seeded), omit
  // the entire block rather than show empty rows.
  if (!program) return null;
  const links: { label: string; url: string }[] = [];
  if (program.rosterUrl) links.push({ label: "Roster", url: program.rosterUrl });
  if (program.scheduleUrl) links.push({ label: "Schedule", url: program.scheduleUrl });
  if (program.statsUrl) links.push({ label: "Stats", url: program.statsUrl });
  if (links.length === 0) return null;
  return (
    <div className="external-links">
      <span className="external-links-label">On NCAA / institution:</span>
      {links.map((l) => (
        <a
          key={l.label}
          className="external-link"
          href={l.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {l.label} ↗
        </a>
      ))}
    </div>
  );
}

function ScheduleTable({
  games,
  forSlug,
  sport,
  division,
  nameFor,
  rankFor,
  confLabelFor,
}: {
  games: Game[];
  forSlug: string;
  sport: string;
  division: string;
  nameFor: (slug: string) => string;
  rankFor: (slug: string) => number | undefined;
  confLabelFor: (slug: string) => string;
}) {
  return (
    <div className="table-scroll">
      <table className="schedule">
        <thead>
          <tr>
            <th>Date</th>
            <th title="Kickoff time in your local timezone (abbreviation shown; TBA when the upstream has not published a time)">Time</th>
            <th></th>
            <th>Opponent</th>
            <th title="Opponent's conference">Conf</th>
            <th className="num" title="Opponent's current overall rank in the standings">Rank</th>
            <th className="num">Result</th>
            <th>Status</th>
            <th className="broadcast-col">Broadcast</th>
          </tr>
        </thead>
        <tbody>
          {games.map((g) => {
            const isHome = sideMatches(g.home, forSlug);
            const opp: GameSide = isHome ? g.away : g.home;
            const outcome = outcomeFor(g, isHome ? "home" : "away");
            const oppConf = opp.conference ?? "";
            const oppRank = rankFor(opp.teamSlug);
            return (
              <tr key={g.id}>
                <td className="date-col">{formatDate(g.date)}</td>
                <td className="time-col muted">{formatKickoff(g.startTime)}</td>
                <td className="ha-col muted">{isHome ? "vs" : "at"}</td>
                <td className="opp-col">
                  <TeamLink
                    slug={opp.teamSlug}
                    sport={sport}
                    division={division}
                    title={`Open ${nameFor(opp.teamSlug)} team page`}
                  >
                    <TeamLogo slug={opp.teamSlug} alt={nameFor(opp.teamSlug)} />
                    <span>{nameFor(opp.teamSlug)}</span>
                  </TeamLink>
                </td>
                <td className="muted">
                  {oppConf ? (
                    <ConferenceLink
                      conference={oppConf}
                      sport={sport}
                      division={division}
                      label={confLabelFor(oppConf)}
                      title={`Show ${confLabelFor(oppConf)} standings`}
                    />
                  ) : (
                    "—"
                  )}
                </td>
                <td className="num">{oppRank ?? "—"}</td>
                <td className={`num result-col ${resultClass(outcome)}`}>
                  {renderResult(g, isHome)}
                </td>
                <td>
                  <StateBadge state={g.state} />
                </td>
                <td className="broadcast-col muted">
                  {renderBroadcastCell({
                    gameID: g.id,
                    channel: g.broadcast,
                    url: g.broadcastUrl,
                  })}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function sideMatches(side: GameSide, slug: string): boolean {
  return (
    side.teamSlug === slug ||
    side.teamSlug.replace(/_/g, "-") === slug ||
    side.teamSlug.replace(/-/g, "_") === slug
  );
}

function resultClass(outcome: "W" | "L" | "T" | ""): string {
  if (outcome === "W") return "winner";
  if (outcome === "L") return "loser";
  return "";
}

function renderResult(g: Game, isHome: boolean): string {
  if (g.state !== "final") return "–";
  const me = isHome ? g.home.score : g.away.score;
  const them = isHome ? g.away.score : g.home.score;
  const outcome = outcomeFor(g, isHome ? "home" : "away");
  return `${outcome} ${me}-${them}`;
}

function StateBadge({ state }: { state: GameState }) {
  return <span className={`state ${state}`}>{state}</span>;
}

// RosterKey is the sortable column union for the roster table.
// Mirrors the Tauri app's roster view: raw stats + derived rates
// (shots missed, SOG%, per-minute rates for shots / SOG / missed /
// goals). Text columns like Class / Height / Hometown aren't
// sortable — they rarely are a useful ordering key.
type RosterKey =
  | "jerseyNumber"
  | "name"
  | "position"
  | "gamesPlayed"
  | "gamesStarted"
  | "minutes"
  | "goals"
  | "assists"
  | "shots"
  | "shotsMissed"
  | "shotsOnGoal"
  | "sogPct"
  | "missedPct"
  | "shotsPerMin"
  | "sogPerMin"
  | "goalsPerMin"
  | "missedPerMin";

// Default sort surfaces the players most involved in game action
// first — minutes played is the single best proxy for "who is in
// the regular rotation" across keepers, starters, and bench. A
// bench row that still shows real stats sorts well below the
// starters so the eye lands on who's actually playing.
const ROSTER_DEFAULT_SORT: SortSpec<RosterKey> = {
  key: "minutes",
  dir: "desc",
};

// Non-soccer rosters hide every stats column, so the minutes-desc
// default has nothing to sort against. Fall back to jersey-asc —
// matches the SQL ORDER BY and reads as "the roster list" rather
// than an arbitrary alphabetization.
const ROSTER_IDENTITY_DEFAULT_SORT: SortSpec<RosterKey> = {
  key: "jerseyNumber",
  dir: "asc",
};

const ROSTER_DEFAULT_DIR: Record<RosterKey, "asc" | "desc"> = {
  jerseyNumber: "asc",
  name: "asc",
  position: "asc",
  gamesPlayed: "desc",
  gamesStarted: "desc",
  minutes: "desc",
  goals: "desc",
  assists: "desc",
  shots: "desc",
  shotsMissed: "desc",
  shotsOnGoal: "desc",
  sogPct: "desc",
  missedPct: "desc",
  shotsPerMin: "desc",
  sogPerMin: "desc",
  goalsPerMin: "desc",
  missedPerMin: "desc",
};

// jerseyAsNumber returns the integer value of a jersey when the whole
// string is digits, otherwise undefined. Non-numeric jerseys ("GK")
// sort to the end regardless of direction via the undefined-last
// rule in sortRoster.
function jerseyAsNumber(j: string): number | undefined {
  if (j === "") return undefined;
  const n = Number(j);
  return Number.isFinite(n) && /^\d+$/.test(j) ? n : undefined;
}

// dash renders an em-dash for undefined so an empty stats row reads
// as "no data" rather than "zero."
function dash(n: number | undefined): string {
  if (n === undefined || n === null) return "—";
  return String(n);
}

// Derived stat helpers — mirror the Tauri reference's formulas:
// M = max(0, SH - SOG); SOG% = SOG / SH; M% = M / SH; *_per_min =
// stat / MIN. Each returns undefined when any input is undefined or
// division is zero so the UI em-dashes rather than rendering "NaN"
// or "Infinity."
function shotsMissedOf(p: RosterPlayer): number | undefined {
  if (p.shots === undefined || p.shotsOnGoal === undefined) return undefined;
  return Math.max(0, p.shots - p.shotsOnGoal);
}
function sogPctOf(p: RosterPlayer): number | undefined {
  if (p.shots === undefined || p.shotsOnGoal === undefined || p.shots === 0) {
    return undefined;
  }
  return p.shotsOnGoal / p.shots;
}
function missedPctOf(p: RosterPlayer): number | undefined {
  const m = shotsMissedOf(p);
  if (m === undefined || p.shots === undefined || p.shots === 0) return undefined;
  return m / p.shots;
}
function perMin(numerator: number | undefined, minutes: number | undefined): number | undefined {
  if (numerator === undefined || minutes === undefined || minutes === 0) return undefined;
  return numerator / minutes;
}
function fmtPct(x: number | undefined): string {
  if (x === undefined) return "—";
  return (x * 100).toFixed(1) + "%";
}
function fmtRate(x: number | undefined): string {
  if (x === undefined) return "—";
  // Rates are small (goals-per-minute hovers around 0.001 even for
  // top scorers); 4 decimals keeps the leading digit visible.
  return x.toFixed(4);
}

function RosterPanel({
  players,
  isSoccer,
}: {
  players: RosterPlayer[];
  // Soccer stats columns (goals, assists, shots, SOG, derived rates)
  // only render for soccer sports. catalog.player_stats is soccer-
  // scoped (LEFT JOIN filtered to soccer-women/soccer-men), so on a
  // football or basketball roster every stat field comes back NULL
  // and rendering the columns reads as "soccer stats, no data" for a
  // sport that has nothing to do with soccer. Hide them entirely
  // until the catalog exposes per-sport stats.
  isSoccer: boolean;
}) {
  const [sort, setSort] = useState<SortSpec<RosterKey>>(
    isSoccer ? ROSTER_DEFAULT_SORT : ROSTER_IDENTITY_DEFAULT_SORT,
  );
  const sorted = useMemo(
    () => sortRoster(players, sort),
    [players, sort],
  );
  const header = (label: string, key: RosterKey, title?: string, align: "left" | "right" = "right") => (
    <SortHeader
      label={label}
      sortKey={key}
      sort={sort}
      onToggle={(k) => setSort(toggleSort(sort, k, ROSTER_DEFAULT_DIR[k]))}
      align={align === "right" ? "right" : undefined}
      title={title}
    />
  );
  return (
    <div className="table-scroll">
      <table className="roster">
        <thead>
          <tr>
            {header("#", "jerseyNumber", "Jersey number")}
            {header("Name", "name", "Player name as printed on the roster", "left")}
            {header("Pos", "position", "Position (GK goalkeeper, D defender, M midfielder, F forward; slashed forms indicate multi-position players)", "left")}
            <th title="Academic year (Fr., So., Jr., Sr., Gr., R- prefix for redshirt)">
              Class
            </th>
            <th title="Height as printed on the roster">Ht</th>
            <th title="Hometown as printed on the roster">Hometown</th>
            {isSoccer && (
              <>
                {header("GP", "gamesPlayed", "Games played — number of games this player appeared in")}
                {header("GS", "gamesStarted", "Games started — number of games this player was in the starting lineup")}
                {header("MIN", "minutes", "Total minutes played across the season")}
                {header("G", "goals", "Goals scored this season")}
                {header("A", "assists", "Assists — passes directly leading to a goal")}
                {header("SH", "shots", "Shots — total shot attempts, on or off target")}
                {header("M", "shotsMissed", "Missed shots — total shots that did not hit the frame (SH − SOG)")}
                {header("SOG", "shotsOnGoal", "Shots on goal — shots that required a save or scored")}
                {header("SOG%", "sogPct", "Shots-on-goal percentage — SOG ÷ SH; higher means more shots forced the keeper to react")}
                {header("M%", "missedPct", "Missed-shot percentage — M ÷ SH; the inverse of SOG%")}
                {header("SH/min", "shotsPerMin", "Shots per minute played — scoring volume normalized by time on the pitch")}
                {header("SOG/min", "sogPerMin", "Shots on goal per minute played")}
                {header("G/min", "goalsPerMin", "Goals per minute played — raw scoring rate")}
                {header("M/min", "missedPerMin", "Missed shots per minute played")}
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {sorted.map((p) => {
            const m = shotsMissedOf(p);
            return (
              <tr key={p.jerseyNumber + "|" + p.name}>
                <td className="num">{p.jerseyNumber}</td>
                <td>{p.name}</td>
                <td>{p.position}</td>
                <td className="muted">{p.classYear ?? "—"}</td>
                <td className="muted">{p.height ?? "—"}</td>
                <td className="muted">{p.hometown ?? "—"}</td>
                {isSoccer && (
                  <>
                    <td className="num">{dash(p.gamesPlayed)}</td>
                    <td className="num">{dash(p.gamesStarted)}</td>
                    <td className="num">{dash(p.minutes)}</td>
                    <td className="num">{dash(p.goals)}</td>
                    <td className="num">{dash(p.assists)}</td>
                    <td className="num">{dash(p.shots)}</td>
                    <td className="num">{dash(m)}</td>
                    <td className="num">{dash(p.shotsOnGoal)}</td>
                    <td className="num">{fmtPct(sogPctOf(p))}</td>
                    <td className="num">{fmtPct(missedPctOf(p))}</td>
                    <td className="num">{fmtRate(perMin(p.shots, p.minutes))}</td>
                    <td className="num">{fmtRate(perMin(p.shotsOnGoal, p.minutes))}</td>
                    <td className="num">{fmtRate(perMin(p.goals, p.minutes))}</td>
                    <td className="num">{fmtRate(perMin(m, p.minutes))}</td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// sortKeyValue projects the sort key onto a comparable number or
// string for one row. Derived columns (percentages, per-minute rates,
// shots missed) compute their value here so the sort sees exactly
// what the cell renders.
function sortKeyValue(p: RosterPlayer, k: RosterKey): number | string | undefined {
  switch (k) {
    case "jerseyNumber":
      return jerseyAsNumber(p.jerseyNumber);
    case "name":
      return p.name;
    case "position":
      return p.position;
    case "shotsMissed":
      return shotsMissedOf(p);
    case "sogPct":
      return sogPctOf(p);
    case "missedPct":
      return missedPctOf(p);
    case "shotsPerMin":
      return perMin(p.shots, p.minutes);
    case "sogPerMin":
      return perMin(p.shotsOnGoal, p.minutes);
    case "goalsPerMin":
      return perMin(p.goals, p.minutes);
    case "missedPerMin":
      return perMin(shotsMissedOf(p), p.minutes);
    default:
      return p[k];
  }
}

function sortRoster(
  players: RosterPlayer[],
  sort: SortSpec<RosterKey>,
): RosterPlayer[] {
  const dir = sort.dir === "asc" ? 1 : -1;
  return [...players].sort((a, b) => {
    const av = sortKeyValue(a, sort.key);
    const bv = sortKeyValue(b, sort.key);
    // Undefined-last regardless of direction so missing-stats rows
    // don't claim the top of a desc sort.
    if (av === undefined && bv === undefined) return 0;
    if (av === undefined) return 1;
    if (bv === undefined) return -1;
    if (typeof av === "number" && typeof bv === "number") {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv)) * dir;
  });
}
